//#region Imports
import React, { useState, useEffect, useMemo } from 'react';
import { View, StyleSheet, SafeAreaView, Platform, Pressable, FlatList, useWindowDimensions, ActivityIndicator, Image } from 'react-native';
import { Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { useTheme, useNavigation } from '@react-navigation/native';
import CustomButton from '../../../components/CustomButton';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import { formatDateForUI } from '../../../constants/FormatDate';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import { useTranslation } from 'react-i18next';
import { useMyTimesheets } from '../../../hooks/useMyTimesheets';
import { useAccountContext } from '../../../hooks/useAccountContext';
import { DEFAULTPROFILE } from '../../../img/DefaultProfile';
import { generateUniqueID } from '../../../components/AssessmentBuilderCmponent/CreateGroup/CreateGroupFunctions';
import { createCommonStyles } from '../../../styles/commonStyles';
import MyTimesheetsDetailsScreen from '../Timesheets/MyTimesheetsDetailsScreen';
import Ionicons from '@react-native-vector-icons/ionicons/static';
//#endregion

const MONTHS = [
    { description: 'January', value: 1 },
    { description: 'February', value: 2 },
    { description: 'March', value: 3 },
    { description: 'April', value: 4 },
    { description: 'May', value: 5 },
    { description: 'June', value: 6 },
    { description: 'July', value: 7 },
    { description: 'August', value: 8 },
    { description: 'September', value: 9 },
    { description: 'October', value: 10 },
    { description: 'November', value: 11 },
    { description: 'December', value: 12 },
];

const TimesheetOnBehalfOf = (props) => {
    //#region Functions definitions, variables and states
    const { width, height } = useWindowDimensions();
    const { colors } = useTheme();
    const navigation = useNavigation();
    const common = createCommonStyles(colors);
    const isLaptopScreen = (width < 1300 ? true : false);
    const styles = makeStyles(colors, height, width, isLaptopScreen);
    const { t, i18n } = useTranslation();
    const { account, managerEmployees } = useAccountContext();
    const { loadNewTimesheet, submitTimesheet, getPhotoByID } = useMyTimesheets();

    // State variables
    const [selectedEmployeeError, setSelectedEmployeeError] = useState("");
    // Initialize with current date
    const today = new Date();
    const currentYearValue = today.getFullYear().toString();
    const currentMonthValue = today.getMonth() + 1;
    const [newYear, setNewYear] = useState(currentYearValue);
    const [newMonth, setNewMonth] = useState(currentMonthValue);
    const [newWeek, setNewWeek] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeesList, setEmployeesList] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [TSError, setTSError] = useState(false);
    const [allUI, setAllUI] = useState({});
    const [isLeftColumnCollapsed, setIsLeftColumnCollapsed] = useState(false);
    const [isTopRowCollapsed, setIsTopRowCollapsed] = useState(false);

    // Year dropdown options
    const currentYear = new Date().getFullYear();
    const allYears = [];
    for (let year = currentYear; year >= 1990; year--) {
        allYears.push({ value: year.toString(), label: year.toString() });
    }
    //#endregion

    //#region GenWeek
    const generateWeeksForMonth = (year, month) => {
        if (!year || !month) return [];
        const yearNum = typeof year === 'string' ? parseInt(year) : year;
        const monthNum = typeof month === 'string' ? parseInt(month) : month;

        const weeks = [];
        const firstDay = new Date(yearNum, monthNum - 1, 1, 12, 0, 0);
        const lastDay = new Date(yearNum, monthNum, 0, 12, 0, 0);

        let currentDate = new Date(yearNum, monthNum - 1, 1, 12, 0, 0);
        const dayOfWeek = currentDate.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        currentDate.setDate(currentDate.getDate() - daysToMonday);

        while (currentDate <= lastDay) {
            const weekStart = new Date(currentDate);
            const weekEnd = new Date(currentDate);
            weekEnd.setDate(weekEnd.getDate() + 6);

            const label = `${formatDateForUI(weekStart.toISOString())}-${formatDateForUI(weekEnd.toISOString())}`;
            weeks.push({
                label: label,
                value: {
                    weekStart: weekStart,
                    weekEnd: weekEnd
                }
            });

            currentDate.setDate(currentDate.getDate() + 7);
        }
        return weeks;
    };

    /*const weekOptions = useMemo(() => {
        return generateWeeksForMonth(newYear, newMonth);
    }, [newYear, newMonth]);*/

    const weekOptions = useMemo(() => {
        return generateWeeksForMonth(newYear, newMonth).map(week => ({
            label: week.label,
            value: week.value.weekStart.toISOString(),
            weekStart: week.value.weekStart,
            weekEnd: week.value.weekEnd,
        }));
    }, [newYear, newMonth]);
    //#endregion

    //#region UseEffects
    // Autoselect current week
    useEffect(() => {
        if (newYear && newMonth && weekOptions.length > 0) {
            // Find the week that contains today's date
            const today = new Date();
            const currentWeek = weekOptions.find(week => {
                const weekStart = new Date(week.value.weekStart);
                const weekEnd = new Date(week.value.weekEnd);
                return today >= weekStart && today <= weekEnd;
            });
            
            if (currentWeek) {
                setNewWeek(currentWeek.value);
            } else if (weekOptions.length > 0) {
                setNewWeek(weekOptions[0].value);
            }
        }
    }, [newYear, newMonth, weekOptions]);

    useEffect(() => {
        if (!selectedEmployee || Object.keys(allUI).length === 0) return;
        const tsUI = allUI[selectedEmployee.employeeId];
        props.setTSUI(tsUI);
        props.setIsEmbedded(true);
    }, [selectedEmployee, allUI])

    useEffect(() => {
        navigation.setOptions({
            title: t('navigate:TTS')
        });
    }, []);

    //#region Fetch Data
    useEffect(() => {
        setLoadingEmployees(true);
        if (managerEmployees.length > 0) {
            // For each managerEmployee, call getPhotoByID to get their photo
            const fetchPhotos = async () => {
                try {
                    const employeesWithPhotos = await Promise.all(
                        managerEmployees.map(async (employee) => {
                            try {
                                const photo = await getPhotoByID(employee.employeeId);
                                return {
                                    ...employee,
                                    image: photo || ''
                                };
                            } catch (error) {
                                //console.error(`Error fetching photo for employee`, error);
                                return {
                                    ...employee,
                                    image: ''
                                };
                            }
                        })
                    );

                    setSelectedEmployeeError("");
                    setEmployeesList(employeesWithPhotos);
                    setSelectedEmployee(employeesWithPhotos[0]);
                    setLoadingEmployees(false);
                } catch (error) {
                    //console.error("Error fetching employee photos:", error);
                    setLoadingEmployees(false);
                    setSelectedEmployeeError(t('common:errorPhoto'));
                }
            };

            fetchPhotos();
        } else {
            setLoadingEmployees(false);
            setSelectedEmployeeError(t('common:noEmployees'));
        }
    }, [managerEmployees]);
    //#endregion
    //#endregion

    //#region CreateTS
    const handleEmployeePress = (employee) => {
        if(employee === selectedEmployee) return;
        props.setIsEmbedded(false);
        props.setTimesheetNonChargableList([]);
        props.setTimesheetChargableList([]);
        setSelectedEmployee(employee);
    };

    const handleCreateWeeklyTimesheet = () => {
        if (newYear === '' || newMonth === '' || !newWeek) {
            setTSError(true);
            return;
        }

        props.setIsEmbedded(false);
        props.setTimesheetNonChargableList([]);
        props.setTimesheetChargableList([]);

        // Loop through all managerEmployees and create timesheets for each
        // Keep a JSON object with all tsUI returned: {employeeId: tsUI, employeeId: tsUI, ...}
        if (account && employeesList.length > 0) {
            const allTimesheetsPromises = employeesList.map((employee) => {
                const selectedWeekObj = weekOptions.find(w => w.value === newWeek);
                let obj = [generateUniqueID(), newYear, newMonth, '', selectedWeekObj, true, employee];
                return loadNewTimesheet(obj)
                    .then((userData) => {
                        return { [employee.employeeId]: userData };
                    })
                    .catch((error) => {
                        return { [employee.employeeId]: null };
                    });
            });

            // Wait for all timesheet creation promises to complete
            Promise.all(allTimesheetsPromises)
                .then((results) => {
                    const allTimesheets = results.reduce((acc, curr) => {
                        return { ...acc, ...curr };
                    }, {});

                    // Check if at least one timesheet was created successfully
                    const hasSuccessfulTimesheets = Object.values(allTimesheets).some(ts => ts !== null);

                    if (hasSuccessfulTimesheets) {
                        setAllUI(allTimesheets)
                        props.setTSUI(allTimesheets[selectedEmployee.employeeId]);
                        props.setisApprover(false);
                        props.setIsMonthlyTimesheet(false);
                        props.setIsWeeklyTimesheet(true);
                        props.setIsEmbedded(true);
                        setTSError(false);

                        showMessage({
                            message: t('common:succCreatingAll'),
                            type: "success",
                            duration: 2000,
                        });
                    } else {
                        showMessage({
                            message: t('common:errCreatingAll'),
                            type: "danger",
                            duration: 2000,
                        });
                    }
                })
                .catch((error) => {
                    showMessage({
                        message: t('common:errCreatingAll'),
                        type: "danger",
                        duration: 2000,
                    });
                });
        }
    };
    //#endregion

    //#region Renderers
    const renderEmployeeCard = ({ item }) => (
        <Pressable onPress={() => handleEmployeePress(item)}>
            <Card style={selectedEmployee && item.employeeId === selectedEmployee.employeeId ? [common.cardstyle, styles.border] : [common.cardstyle]}>
                <View style={styles.employeeCardContent}>
                    <Image
                        style={styles.employeeImage}
                        source={{ uri: `data:image/png;base64,${item.image === undefined || item.image === '' ? DEFAULTPROFILE : item.image}` }}
                    />
                    {!isLeftColumnCollapsed && (
                        <View style={styles.employeeInfo}>
                            <CustomLabel
                                value={item.name}
                                type='MAJOR'
                            />
                        </View>
                    )}
                </View>
            </Card>
        </Pressable>
    );

    const RenderWeeklyTimesheetSelection = () => {
        // Memoize options to prevent recreation on every render
        const yearOptions = useMemo(() => [...allYears], []);
        const monthOptions = useMemo(() => [...MONTHS.map(m => ({ label: m.description, value: m.value }))], []);

        return (
            <View>
                {/* Header with Toggle Button - Only on Laptop */}
                {isLaptopScreen && (
                    <View style={styles.topRowHeader}>
                        <Pressable
                            onPress={() => setIsTopRowCollapsed(!isTopRowCollapsed)}
                        >
                            <Ionicons
                                name={isTopRowCollapsed ? "chevron-down" : "chevron-up"}
                                size={20}
                                color={colors.text_primary}
                            />
                        </Pressable>
                        <CustomLabel
                            value={t('common:timesheetSelection')}
                            type="SUBTITLE"
                            style={{ fontWeight: 'bold' }}
                        />
                    </View>
                )}
                {(!isLaptopScreen || !isTopRowCollapsed) && (
                    <View style={{ padding: 5, flexDirection: 'row' }}>
                        {/* Year Dropdown */}
                        <View style={[styles.inputContainer, { zIndex: 20, flexDirection: 'row', alignSelf: 'center' }]}>
                            <View style={{ alignItems: 'flex-start', marginHorizontal: 25 }}>
                                <CustomLabel value={t('common:year') + ': '} type="SUBTITLE" />
                            </View>
                            <CustomDropDown
                                defValue={newYear}
                                options={yearOptions}
                                value={newYear}
                                setValue={setNewYear}
                                type={TSError && newYear === '' ? "ERROR" : "MYPROFILE"}
                            />
                        </View>
                        {/* Month Dropdown */}
                        <View style={[styles.inputContainer, { zIndex: 10, flexDirection: 'row', alignSelf: 'center' }]}>
                            <View style={{ alignItems: 'flex-start', marginHorizontal: 20 }}>
                                <CustomLabel value={t('common:month') + ': '} type="SUBTITLE" />
                            </View>
                            <CustomDropDown
                                defValue={newMonth}
                                options={monthOptions}
                                value={newMonth}
                                setValue={setNewMonth}
                                type={TSError && newMonth === '' ? "ERROR" : "MYPROFILE"}
                            />
                        </View>
                        {/* Week Dropdown */}
                        <View style={[styles.inputContainer, { zIndex: 5, flexDirection: 'row', alignSelf: 'center' }]}>
                            <View style={{ alignItems: 'flex-start', marginHorizontal: 20 }}>
                                <CustomLabel value={t('common:week') + ': '} type="SUBTITLE" />
                            </View>
                            <CustomDropDown
                                defValue={newWeek}
                                options={weekOptions}
                                value={newWeek}
                                setValue={setNewWeek}
                                type={TSError && !newWeek ? "ERROR" : "MYPROFILE"}
                                disabled={!newYear || !newMonth}
                            />
                        </View>
                        {/* Select Submit Buttons */}
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
                            <View style={{ flexDirection: 'row', justifyContent: 'center', paddingHorizontal: 50 }}>
                                <CustomButton
                                    text={t('common:select')}
                                    onPress={handleCreateWeeklyTimesheet}
                                    type="BUTTON_SAVE"
                                    textTop='0'
                                    isGradient={true}
                                    sz={15}
                                />
                            </View>
                            <View style={styles.submitButtonContainer}>
                                <CustomButton
                                    text={t('common:submit')}
                                    onPress={SubmitTS}
                                    type="BUTTON_SAVE"
                                    textTop='0'
                                    isGradient={true}
                                    sz={15}
                                />
                            </View>
                        </View>
                    </View>
                )}
            </View>
        );
    };
    //#endregion

    //#region SubmitTS
    const SubmitTS = async () => {
        if (!allUI || Object.keys(allUI).length === 0) {
            showMessage({
                message: t('common:noSubmit'),
                type: "warning",
                duration: 2000,
            });
            return;
        }

        try {
            const results = [];
            const employeeIds = Object.keys(allUI);

            for (const employeeId of employeeIds) {
                const tsUI = allUI[employeeId];

                try {
                    const result = await submitTimesheet(tsUI);

                    results.push({ employeeId, success: result });

                    // Add 300ms delay between submissions
                    if (employeeIds.indexOf(employeeId) < employeeIds.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 300));
                    }
                } catch (error) {
                    console.error(error);
                    results.push({ employeeId, success: false });
                }
            }

            const allSuccessful = results.every(r => r.success === 200);

            if (allSuccessful) {
                showMessage({
                    message: t("common:submittedSuccess"),
                    type: "success",
                    duration: 3000,
                });

                setAllUI({});
                setNewYear(currentYearValue);
                setNewMonth(currentMonthValue);
                setNewWeek(null);
                props.setIsEmbedded(false);
            } else {
                showMessage({
                    message: t("common:noAllSubmitSucc"),
                    type: "danger",
                    duration: 3000,
                });
            }
        } catch (error) {
            console.log(error);
            showMessage({
                message: t("common:errorSubmit"),
                type: "danger",
                duration: 3000,
            });
        }
    };
    //#endregion

    //#region Return
    return (
        <SafeAreaView style={styles.main}>
            {/* Row 1: Selection Controls */}
            <View style={styles.row1}>
                <RenderWeeklyTimesheetSelection />
            </View>

            {/* Left Column - Employee List with Collapse/Expand */}
            <View style={styles.row2}>
                <SafeAreaView style={isLeftColumnCollapsed ? styles.leftColumnCollapsed : styles.leftColumnExpanded}>
                    {/* Collapse/Expand Button */}
                    <Pressable
                        style={styles.collapseExpandButton}
                        onPress={() => setIsLeftColumnCollapsed(!isLeftColumnCollapsed)}
                    >
                        <Ionicons
                            name={isLeftColumnCollapsed ? "arrow-forward" : "arrow-back"}
                            size={24}
                            color={colors.calendarArrow}
                        />
                    </Pressable>

                    {loadingEmployees ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator animating={true} color={colors.blue} />
                        </View>
                    ) : employeesList.length > 0 ? (
                        <FlatList
                            data={employeesList}
                            renderItem={renderEmployeeCard}
                            keyExtractor={(item) => item.name}
                            scrollEnabled={true}
                            contentContainerStyle={styles.employeeListContent}
                        />
                    ) : (
                        <View style={styles.loadingContainer}>
                            <CustomLabel
                                value={selectedEmployeeError || "No employees available"}
                                type='SUBTITLE'
                            />
                        </View>
                    )}
                </SafeAreaView>

                {/* Right Column - Timesheet Details */}
                <View style={styles.rightColumn}>
                    {props.isEmbedded && (
                        <MyTimesheetsDetailsScreen
                            isMonthlyTimesheet={props.isMonthlyTimesheet}
                            isWeekly={props.isWeeklyTimesheet}
                            startDate={props.startDate}
                            endDate={props.endDate}
                            selectMyTimesheetsScreen={() => { props.selectMyTimesheetsScreen() }}
                            loadTimesheetDetails={(tsUI) => { props.loadTimesheetDetails(tsUI) }}
                            loadTimesheetInfo={(tsUI) => { props.loadTimesheetInfo(tsUI) }}
                            tsUI={props.tsUI}
                            isApprover={props.isApprover}
                            timesheetNonChargableList={props.timesheetNonChargableList}
                            timesheetChargableList={props.timesheetChargableList}
                            tsExpenses={props.tsExpenses}
                            expCode={props.expCode}
                            jobCodeChargOptions={props.jobCodeChargOptions}
                            jobCodeNonChargOptions={props.jobCodeNonChargOptions}
                            usersJobCodeChargOptions={props.usersJobCodeChargOptions}
                            usersJobCodeNonChargOptions={props.usersJobCodeNonChargOptions}
                            usersChargPhaseCodeOptions={props.usersChargPhaseCodeOptions}
                            usersNonChargPhaseCodeOptions={props.usersNonChargPhaseCodeOptions}
                            setUsersNonChargPhaseCodeOptions={(val) => { props.setUsersNonChargPhaseCodeOptions(val) }}
                            setUsersChargPhaseCodeOptions={(val) => { props.setUsersChargPhaseCodeOptions(val) }}
                            usersExpCodeOptions={props.usersExpCodeOptions}
                            companiesOptions={props.companiesOptions}
                            tsInfo={props.tsInfo}
                            timeScheme={props.timeScheme}
                            userTimesheetsList={props.userTimesheetsList}
                            setTSInfo={(val) => { props.setTSInfo(val) }}
                            isEmbedded={props.isEmbedded}
                            isLeftColumnCollapsed={isLeftColumnCollapsed}
                        />
                    )}
                </View>
            </View>
        </SafeAreaView>
    )
}

const makeStyles = (colors, height, width, isLaptopScreen) => StyleSheet.create({
    leftColumn: {
        flex: 0.2,
        borderRightWidth: 1,
        borderRightColor: colors.border_card,
        paddingRight: 15,
        position: 'relative',
    },
    leftColumnCollapsed: {
        flex: isLaptopScreen ? 0 : 0.035,
        borderRightWidth: 1,
        borderRightColor: colors.border_card,
        paddingRight: 5,
        position: 'relative',
    },
    leftColumnExpanded: {
        flex: isLaptopScreen ? 0.15 : 0.2,
        borderRightWidth: 1,
        borderRightColor: colors.border_card,
        paddingRight: 15,
        position: 'relative',
    },
    rightColumn: {
        flex: 1,
        paddingLeft: 15,
    },
    collapseExpandButton: {
        position: 'absolute',
        right: -15,
        top: '50%',
        transform: [{ translateY: -12 }],
        zIndex: 10,
        backgroundColor: colors.background_white,
        borderRadius: 20,
        padding: 5,
        elevation: 3,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.2,
        shadowRadius: 3,
    },
    submitButtonContainer: {
        marginLeft: 'auto',
        paddingHorizontal: 20,
    },
    topRowHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    row1: {
        height: 'auto',
        paddingHorizontal: 20,
        borderBottomWidth: 1,
        borderBottomColor: colors.border_card,
        zIndex: 100
    },
    row2: {
        flex: 1,
        flexDirection: 'row',
        paddingHorizontal: 20,
        paddingVertical: 15,
    },
    main: {
        flex: 1,
        borderRadius: 35,
        height: "100%",
    },
    contentContainer: {
        flex: 1,
    },
    employeeListContent: {
        paddingVertical: 10,
    },
    employeeCardContent: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 12,
    },
    employeeImage: {
        width: 40,
        height: 40,
        borderRadius: 20,
        marginRight: 12,
        borderWidth: 1,
        borderColor: colors.border_card,
    },
    employeeInfo: {
        flex: 1,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    icons: {
        fontSize: 22,
        color: colors.text_primary,
        marginLeft: Platform.OS === 'web' ? 0 : 1
    },
    loading: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#4a4a4a',
        height: '100%',
        opacity: 0.8,
    },
    inputContainer: {
        marginTop: 10,
        marginBottom: 20,
    },
    border: {
        borderColor: colors.buttons_primary,
        borderWidth: 2,
    },
});

export default TimesheetOnBehalfOf;